import React, { useState } from 'react';
import { Button } from '@components/primitives/Button';
import { useAppointments } from '@features/appointments';
import { SettingsIcon } from '@jotpsych/ui/icons/SettingsIcon';
import {
  Box,
  Divider,
  FormControlLabel,
  IconButton,
  Menu,
  Radio,
  RadioGroup,
  Slider,
  Tooltip,
  Typography,
} from '@mui/material';

interface CalendarSettingsMenuProps {
  variant?: 'icon' | 'fullWidth';
}

type CalendarStep = 1 | 5 | 10 | 15;
type CalendarTimeslots = 1 | 2 | 3 | 4 | 6 | 12;

const ROW_DURATION_OPTIONS = [1, 5, 10, 15, 20, 30, 60, 120] as const;
type RowDurationOption = (typeof ROW_DURATION_OPTIONS)[number];

const ROW_DURATION_MARKS = ROW_DURATION_OPTIONS.map((duration, index) => ({
  value: index,
  label: duration === 60 ? '1h' : duration === 120 ? '2h' : `${duration}m`,
}));

// Type guard for valid timeslot values
const isValidTimeslot = (value: number): value is CalendarTimeslots => {
  return [1, 2, 3, 4, 6, 12].includes(value);
};

// Utility function to get closest valid row duration
const getClosestValidDuration = (duration: number): number => {
  return ROW_DURATION_OPTIONS.reduce((prev, curr) =>
    Math.abs(curr - duration) < Math.abs(prev - duration) ? curr : prev,
  );
};

const isRowDurationOption = (value: number): value is RowDurationOption =>
  ROW_DURATION_OPTIONS.includes(value as RowDurationOption);

const getRowDurationIndex = (duration: number): number => {
  const rowDuration = isRowDurationOption(duration)
    ? duration
    : getClosestValidDuration(duration);
  return ROW_DURATION_OPTIONS.indexOf(rowDuration as RowDurationOption);
};

const getCalendarGranularityForRowDuration = (
  rowDuration: RowDurationOption,
  currentStep: CalendarStep,
): { step: CalendarStep; timeslots: CalendarTimeslots } => {
  const currentTimeslots = rowDuration / currentStep;

  if (Number.isInteger(currentTimeslots) && isValidTimeslot(currentTimeslots)) {
    return { step: currentStep, timeslots: currentTimeslots };
  }

  switch (rowDuration) {
    case 1:
      return { step: 1, timeslots: 1 };
    case 5:
      return { step: 5, timeslots: 1 };
    case 10:
      return { step: 10, timeslots: 1 };
    case 15:
      return { step: 5, timeslots: 3 };
    case 20:
      return { step: 5, timeslots: 4 };
    case 30:
      return { step: 5, timeslots: 6 };
    case 60:
      return { step: 5, timeslots: 12 };
    case 120:
      return { step: 10, timeslots: 12 };
    default:
      return { step: 15, timeslots: 4 };
  }
};

const CalendarSettingsMenu: React.FC<CalendarSettingsMenuProps> = ({ variant = 'icon' }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const { calendarGranularity, setCalendarGranularity } = useAppointments();

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  // Calculate the current row duration based on step and timeslots
  const currentRowDuration = calendarGranularity.step * calendarGranularity.timeslots;

  const handleRowDurationChange = (_event: Event, value: number | number[]) => {
    const optionIndex = Array.isArray(value) ? value[0] : value;
    const rowDuration = ROW_DURATION_OPTIONS[optionIndex];

    if (!rowDuration) return;
    setCalendarGranularity(
      getCalendarGranularityForRowDuration(rowDuration, calendarGranularity.step),
    );
  };

  const handleStepChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newStep = parseInt(event.target.value, 10) as CalendarStep;
    const currentRowDuration = calendarGranularity.step * calendarGranularity.timeslots;
    const targetRowDuration = getClosestValidDuration(currentRowDuration) as RowDurationOption;

    setCalendarGranularity(getCalendarGranularityForRowDuration(targetRowDuration, newStep));
  };

  return (
    <>
      {variant === 'fullWidth' ? (
        <Button
          variant="outlined"
          onClick={handleClick}
          startIcon={<SettingsIcon />}
          fullWidth
          sx={{
            color: '#535976',
            borderColor: 'rgba(0, 13, 77, 0.45)',
            justifyContent: 'flex-start',
            px: 2,
            py: 1,
            '&:hover': {
              borderColor: '#535976',
              backgroundColor: 'rgba(83, 89, 118, 0.04)',
            },
          }}
        >
          Calendar Settings
        </Button>
      ) : (
        <Tooltip title="Settings" placement="bottom" arrow={false}>
          <IconButton
            onClick={handleClick}
            size="small"
            aria-label="Calendar settings"
            sx={{
              color: '#535976',
              '&:hover': {
                backgroundColor: 'rgba(83, 89, 118, 0.08)',
              },
            }}
          >
            <SettingsIcon fontSize="medium" />
          </IconButton>
        </Tooltip>
      )}

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        sx={{
          '& .MuiPaper-root': {
            minWidth: 220,
            mt: 1,
          },
        }}
      >
        <Box sx={{ px: 2, py: 1.5 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Settings
          </Typography>
        </Box>

        <Divider />

        <Box sx={{ px: 2, py: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Time slot rows
          </Typography>
          <Slider
            aria-label="Time slot row zoom"
            value={getRowDurationIndex(currentRowDuration)}
            onChange={handleRowDurationChange}
            min={0}
            max={ROW_DURATION_OPTIONS.length - 1}
            step={null}
            marks={ROW_DURATION_MARKS}
            valueLabelDisplay="auto"
            valueLabelFormat={value => {
              const duration = ROW_DURATION_OPTIONS[value];
              if (duration === 60) return '1 hour';
              if (duration === 120) return '2 hours';
              return `${duration} minutes`;
            }}
            sx={{
              mx: 1,
              mt: 2,
              mb: 3,
              width: 'calc(100% - 16px)',
              '& .MuiSlider-markLabel': {
                fontSize: 11,
              },
            }}
          />
        </Box>

        <Divider />

        <Box sx={{ px: 2, py: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Drag precision
          </Typography>
          <RadioGroup value={calendarGranularity.step} onChange={handleStepChange}>
            <FormControlLabel
              value={1}
              control={<Radio size="small" />}
              label="1 minute"
              sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.875rem' } }}
            />
            <FormControlLabel
              value={5}
              control={<Radio size="small" />}
              label="5 minutes"
              sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.875rem' } }}
            />
            <FormControlLabel
              value={10}
              control={<Radio size="small" />}
              label="10 minutes"
              sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.875rem' } }}
            />
            <FormControlLabel
              value={15}
              control={<Radio size="small" />}
              label="15 minutes"
              sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.875rem' } }}
            />
          </RadioGroup>
        </Box>
      </Menu>
    </>
  );
};

export default CalendarSettingsMenu;
